import { FC, useState } from "react";

import Stack from "@mui/material/Stack";
import {
  AvailablePaymentCurrency,
  DashboardPaymentStatus,
  FormType,
  ReceiptStatus,
  SubscriptionRecurrenceInterval,
} from "@simplyk/common";

import { CommandFromQuery, DonorPaymentControl, PaymentFromQuery, ReceiptFromQuery } from "../types";

import { EditDonationAmountDialog } from "./EditDonationAmountDialog/EditDonationAmountDialog";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton, DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardChip } from "@/dashboard-design-system/DashboardChip/DashboardChip";
import { DashboardChipVibe } from "@/dashboard-design-system/DashboardChip/types";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardTooltip";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Cross, Download, MembershipCard, Ticket } from "@/icons/outlined";

interface HeaderProps {
  payment: PaymentFromQuery;
  formattedAmount: string;
  isMembership: boolean;
  isSweepstakes?: boolean;
  ticketCancellationStatus: { allTicketsCanceled: boolean; someTicketsCanceled: boolean };
  receipt: ReceiptFromQuery | null | undefined;
  command: CommandFromQuery | null | undefined;
  clickOnDownloadReceipt: () => void;
  clickDownloadETicket: () => void;
  onClose?: () => void;
  onEditAmount?: (newAmount: number) => Promise<boolean>;
  editAmountIsLoading?: boolean;
  currentDonationAmount?: number;
  initialControl?: DonorPaymentControl;
}

export const Header: FC<HeaderProps> = ({
  payment,
  formattedAmount,
  isMembership,
  isSweepstakes = false,
  ticketCancellationStatus,
  receipt,
  command,
  clickOnDownloadReceipt,
  clickDownloadETicket,
  onClose,
  onEditAmount,
  editAmountIsLoading = false,
  currentDonationAmount,
  initialControl,
}) => {
  const { t } = useTranslate();
  const { isoLocale } = useLocaleContext();
  const { logAmplitudeEvent } = useAmplitude();
  const isActiveSubscription = payment.subscriptionStatus === "Active" || payment.subscriptionStatus === "PastDue";
  const canEditAmount =
    isActiveSubscription &&
    payment.formType === FormType.DonationForm &&
    Boolean(onEditAmount && currentDonationAmount);
  const [editAmountDialogOpen, setEditAmountDialogOpen] = useState(initialControl === "change_amount" && canEditAmount);

  const formattedDate = new Date(payment.createdAtUtc).toLocaleDateString(isoLocale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  const formattedRefundDate = payment.refundCreatedAtUtc
    ? new Date(payment.refundCreatedAtUtc).toLocaleDateString(isoLocale, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

  const isReceiptPending = receipt?.status === ReceiptStatus.Pending;
  const isReceiptDownloadable =
    receipt && !isReceiptPending && receipt.status !== ReceiptStatus.Canceled && receipt.fileUrl;
  const isTicketing = payment.formType === FormType.Ticketing;
  const hasETicket = isTicketing && Boolean(command?.eTicketUrl);
  const { statusLabel, statusVibe } = getDonorStatusChip(payment, t);

  const receiptDisabledTooltip = (() => {
    if (isReceiptPending && isActiveSubscription) {
      return t("dashboard", "donorDashboard.transactions.recurringReceiptPendingTooltip");
    }
    if (isReceiptPending) {
      return t("dashboard", "receiptLink.receiptPendingTooltip");
    }
    return "";
  })();

  return (
    <Stack width="100%" gap={3}>
      {onClose && (
        <DashboardIconButton onClick={onClose} vibe="neutral" size="medium" sx={{ alignSelf: "flex-start" }}>
          <Cross />
        </DashboardIconButton>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" width="100%">
        <Stack alignItems="flex-start">
          <Stack direction="row" sx={{ mb: 1 }}>
            <DashboardChip label={statusLabel} vibe={statusVibe} variant="outlined" />
          </Stack>

          <DashboardTypography variant="h3" vibe="text-neutral-intense" sx={{ fontWeight: 700, mb: 0.5 }}>
            {formattedAmount}
          </DashboardTypography>

          <DashboardTypography variant="body2" vibe="text-neutral-quiet">
            {formattedDate}
          </DashboardTypography>

          {formattedRefundDate && (
            <DashboardTypography variant="body2" vibe="text-neutral-quiet" sx={{ mt: 0.5 }}>
              {t("dashboard", "donorTransactionDetailDrawer.refundCreation", { date: formattedRefundDate })}
            </DashboardTypography>
          )}

          {canEditAmount && (
            <DashboardButton
              variant="outlined"
              size="small"
              vibe="neutral"
              sx={{ mt: 2, alignSelf: "flex-start" }}
              onClick={() => {
                logAmplitudeEvent(AmplitudeEvents.DonorTransactionDrawerClickEditTheAmount);
                setEditAmountDialogOpen(true);
              }}
            >
              {t("dashboard", "donorTransactionDetailDrawer.changeAmount")}
            </DashboardButton>
          )}
        </Stack>

        <Stack alignItems="flex-end" gap={1.5}>
          <Stack direction="row" gap={1}>
            {hasETicket && (
              <DashboardButton
                variant="outlined"
                size="medium"
                vibe="neutral"
                startIcon={isMembership ? <MembershipCard /> : <Ticket />}
                href={command?.eTicketUrl ?? undefined}
                target="_blank"
                onClick={clickDownloadETicket}
                disabled={ticketCancellationStatus.allTicketsCanceled}
              >
                {isMembership
                  ? t("dashboard", "donorTransactionDetailDrawer.viewMemberCard")
                  : t(
                      "dashboard",
                      isSweepstakes ? "sweepstakes.viewEntries" : "donorTransactionDetailDrawer.viewTickets"
                    )}
              </DashboardButton>
            )}
            {receipt && (
              <DashboardTooltip title={receiptDisabledTooltip}>
                <span>
                  <DashboardButton
                    variant="outlined"
                    size="medium"
                    vibe="neutral"
                    startIcon={<Download />}
                    href={isReceiptDownloadable ? receipt.fileUrl! : undefined}
                    target="_blank"
                    onClick={clickOnDownloadReceipt}
                    disabled={!isReceiptDownloadable}
                  >
                    {t("dashboard", "donorTransactionDetailDrawer.viewReceipt")}
                  </DashboardButton>
                </span>
              </DashboardTooltip>
            )}
          </Stack>
          {(ticketCancellationStatus.someTicketsCanceled ||
            ticketCancellationStatus.allTicketsCanceled ||
            receipt?.status === ReceiptStatus.Canceled) && (
            <Stack alignItems="flex-end" gap={1}>
              {(ticketCancellationStatus.someTicketsCanceled || ticketCancellationStatus.allTicketsCanceled) && (
                <DashboardChip
                  label={t(
                    "dashboard",
                    isSweepstakes ? "sweepstakes.entriesCanceled" : "donorDashboard.transactions.ticketsCanceled"
                  )}
                  vibe="danger"
                  variant="filled"
                  shape="rounded"
                />
              )}
              {receipt?.status === ReceiptStatus.Canceled && (
                <DashboardChip
                  label={t("dashboard", "donorDashboard.transactions.receiptCanceled")}
                  vibe="danger"
                  variant="filled"
                  shape="rounded"
                />
              )}
            </Stack>
          )}
        </Stack>
      </Stack>

      {onEditAmount && currentDonationAmount && (
        <EditDonationAmountDialog
          open={editAmountDialogOpen}
          onClose={() => setEditAmountDialogOpen(false)}
          onConfirm={onEditAmount}
          isLoading={editAmountIsLoading}
          currentAmount={currentDonationAmount}
          currency={payment.currency as AvailablePaymentCurrency}
          recurrenceInterval={payment.recurrenceInterval as SubscriptionRecurrenceInterval | null}
        />
      )}
    </Stack>
  );
};

type TranslateFunction = ReturnType<typeof useTranslate>["t"];

const getDonorStatusChip = (
  payment: PaymentFromQuery,
  t: TranslateFunction
): { statusLabel: string; statusVibe: DashboardChipVibe } => {
  const isFullyRefunded =
    payment.transferrableAmount > 0 &&
    payment.transferrableAmount === payment.refundedAmount + (payment.inProgressRefundAmount || 0);

  if (payment.inProgressRefundAmount && payment.inProgressRefundAmount > 0) {
    return { statusLabel: t("dashboard", "paymentsPage.table.status.RefundInProgress"), statusVibe: "warning" };
  }

  if (isFullyRefunded) {
    return { statusLabel: t("dashboard", "paymentsPage.table.status.Refunded"), statusVibe: "warning" };
  }

  if (payment.displayStatus === DashboardPaymentStatus.Failed) {
    return { statusLabel: t("dashboard", "paymentsPage.table.status.Failed"), statusVibe: "danger" };
  }

  if (payment.displayStatus === DashboardPaymentStatus.Due) {
    return { statusLabel: t("dashboard", "daf.due"), statusVibe: "warning" };
  }

  if (payment.displayStatus === DashboardPaymentStatus.Processing) {
    return { statusLabel: t("dashboard", "paymentsPage.table.status.Processing"), statusVibe: "positive" };
  }

  if (payment.displayStatus === DashboardPaymentStatus.Disputed) {
    return { statusLabel: t("dashboard", "paymentsPage.table.status.Disputed"), statusVibe: "warning" };
  }

  if (payment.displayStatus === DashboardPaymentStatus.DisputeLost) {
    return { statusLabel: t("dashboard", "donorTransactionDetailDrawer.disputeWon"), statusVibe: "warning" };
  }

  if (payment.displayStatus === DashboardPaymentStatus.PastDue) {
    return { statusLabel: t("dashboard", "paymentsPage.table.status.PastDue"), statusVibe: "warning" };
  }

  return { statusLabel: t("dashboard", "paymentsPage.table.status.Succeeded"), statusVibe: "positive" };
};
